/**
 * @file src/offline/registerOfflineWorker.js
 * @description Installing the worker that makes a downloaded collection survive a closed tab.
 *
 * Registered by hand rather than by the plugin's injected snippet, for two reasons worth keeping.
 * The first is timing: the snippet runs at parse time on every page, and this app's first frame is
 * already competing with a renderer, an audio graph and a collection fetch — a worker install that
 * kicks off a precache of the whole build is exactly the thing to do AFTER that, not during it. The
 * second is honesty about dev: the plugin does not build a worker for the dev server, and a
 * registration that silently 404s every reload is noise nobody reads.
 *
 * ## What is deliberately NOT here
 *
 * No update prompt. The worker is built with `skipWaiting` off, so a new build waits for every tab
 * to close and takes over on the next cold start. Offering a person a "reload to update" button
 * mid-session, in a player, is offering to interrupt what they are listening to; the update is not
 * urgent and the next launch is soon enough.
 *
 * No unregister path. A worker that has been asked to keep a library is not a thing to remove
 * behind the person's back — removing downloads is what removes downloads.
 */

import { LIBRARY_CHANGED_MESSAGE } from './offlineAssetIndex.js';

/** Where the generated worker lands. Matches the plugin's default output. */
const WORKER_URL = '/sw.js';

let registration = null;

/** Whether this browser can hold a worker at all. */
export function supportsOfflineWorker() {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/**
 * Install the worker, once, after the page has settled.
 *
 * Never rejects. A worker that will not install means offline boot is unavailable — a feature
 * quietly missing — and there is nothing about that worth taking a boot down for. The reason is
 * logged because the difference between "not registered" and "registered and failing" is the first
 * thing anybody debugging this will want.
 *
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerOfflineWorker() {
  if (registration) return registration;
  if (!supportsOfflineWorker()) return null;

  // The worker is a build artifact. In dev there is none, and asking for it gets a 404 that Vite
  // answers with `index.html` — which the browser then refuses as a worker, once per reload.
  if (import.meta.env?.DEV) return null;

  // A worker registered from a page served over plain HTTP on a real host is refused outright;
  // localhost is the standing exception. Checking first turns a console error into a known state.
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    console.info('[offline] No service worker: this origin is not a secure context.');
    return null;
  }

  try {
    registration = await navigator.serviceWorker.register(WORKER_URL, { scope: '/' });
    return registration;
  } catch (error) {
    console.warn('[offline] Service worker registration failed; downloads will not survive a closed tab.', error);
    registration = null;
    return null;
  }
}

/**
 * Whether a worker is controlling THIS page right now.
 *
 * Not the same question as "is one registered". The very first load after an install has a
 * registered worker and no controller — nothing it serves is being served yet — and that is the
 * load on which a person is most likely to be told they can now go offline. They cannot, until the
 * next one. Anything that says "ready for offline" has to ask this, not the registration.
 */
export function isOfflineWorkerControlling() {
  return supportsOfflineWorker() && Boolean(navigator.serviceWorker.controller);
}

/**
 * Tell the controlling worker that what is downloaded has changed.
 *
 * The worker keeps an in-memory map of `remote url → pinned cache entry`, built from the stored
 * manifests. It rebuilds that map on a miss, but only if it is already stale — otherwise a miss
 * (most cross-origin requests are not downloaded assets) would mean a store read per request. This
 * closes the one gap that leaves: downloading a collection and going offline straight afterwards,
 * which is the main way anybody uses this at all.
 *
 * Best effort by design. No controller means nothing is being served through a worker anyway, and a
 * `postMessage` that will not send changes nothing about whether the download worked.
 */
export function notifyOfflineWorkerLibraryChanged() {
  if (!isOfflineWorkerControlling()) return;
  try {
    navigator.serviceWorker.controller.postMessage({ type: LIBRARY_CHANGED_MESSAGE });
  } catch {
    // A worker that will not take a message re-reads the manifests on its own soon enough.
  }
}
